import { useEffect, useMemo, useState } from "react";
import { LineConfig } from "../../../components/Charts";
import chartDataFetcher, { type P1RawDataPoint } from "../../../services/api/ChartDataFetch";

export type Sistema = "CAT" | "ES" | "EU";

export interface P1FilterValues {
  yearRange?: [number, number];
  sistema: Sistema;
  ambit: string;
}

export interface P1ChartPoint {
  curs_academic: string;
  [key: string]: string | number;
}

const AMBIT_COLORS: Record<string, string> = {
  "Arts i humanitats":              "#E74C3C",
  "Ciències":                       "#3498DB",
  "Ciències de la salut":           "#2ECC71",
  "Ciències socials i jurídiques":  "#9B59B6",
  "Enginyeria i arquitectura":      "#1ABC9C",
};

const FALLBACK_COLORS = [
  "#4F81BD", "#C0504D", "#9BBB59", "#8064A2",
  "#4BACC6", "#F79646", "#2C4770", "#BE4B48",
];

const SISTEMA_COLORS: Partial<Record<Sistema, string>> = {
  CAT: "#3498DB",
  ES:  "#E74C3C",
  EU:  "#2ECC71",
};

const SISTEMA_LABELS: Partial<Record<Sistema, string>> = {
  CAT: "Catalunya",
  ES:  "Espanya",
  EU:  "Europa",
};

function pivotByAmbit(filtered: P1RawDataPoint[]): P1ChartPoint[] {
  const cursosOrdenats = Array.from(
    new Set(filtered.map((d) => d.curs_academic))
  ).sort();

  return cursosOrdenats.map((curs) => {
    const point: P1ChartPoint = { curs_academic: curs };
    filtered
      .filter((d) => d.curs_academic === curs)
      .forEach((d) => { point[d.ambit_estudi] = d.ratio; });
    return point;
  });
}

function buildAmbitLines(filtered: P1RawDataPoint[]): LineConfig[] {
  const ambits = Array.from(
    new Set(filtered.map((d) => d.ambit_estudi))
  ).sort();

  return ambits.map((ambit, i) => ({
    key:   ambit,
    label: ambit,
    color: AMBIT_COLORS[ambit] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));
}

function pivotBySistema(filtered: P1RawDataPoint[]): P1ChartPoint[] {
  const cursosOrdenats = Array.from(
    new Set(filtered.map((d) => d.curs_academic))
  ).sort();

  return cursosOrdenats.map((curs) => {
    const point: P1ChartPoint = { curs_academic: curs };
    filtered
      .filter((d) => d.curs_academic === curs)
      .forEach((d) => {
        const label = SISTEMA_LABELS[d.sistema as Sistema] ?? d.sistema;
        point[label] = d.ratio;
      });
    return point;
  });
}

function buildSistemaLines(filtered: P1RawDataPoint[]): LineConfig[] {
  const sistemes = Array.from(
    new Set(filtered.map((d) => d.sistema as Sistema))
  ).sort() as Sistema[];

  return sistemes.map((s, i) => ({
    key:   SISTEMA_LABELS[s] ?? s,
    label: SISTEMA_LABELS[s] ?? s,
    color: SISTEMA_COLORS[s] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length],
  }));
}

export function useP1Data(filters: P1FilterValues) {
  const [rawData, setRawData] = useState<P1RawDataPoint[]>(
    () => chartDataFetcher.getCachedSync<P1RawDataPoint[]>("p1") ?? []
  );
  const [loading, setLoading] = useState(
    () => chartDataFetcher.getCachedSync<P1RawDataPoint[]>("p1") === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartDataFetcher.getCachedSync("p1") !== null) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP1Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const availableAmbits = useMemo(() => {
    return Array.from(new Set(rawData.map((d) => d.ambit_estudi))).sort();
  }, [rawData]);

  const yearFiltered = useMemo(() => {
    const range = filters.yearRange;
    if (!range) return rawData;
    return rawData.filter((d) => {
      const year = parseInt(d.curs_academic.split("/")[0], 10);
      return year >= range[0] && year <= range[1];
    });
  }, [rawData, filters.yearRange]);

  const { chart1Data, chart1Lines } = useMemo(() => {
    const filtered = yearFiltered.filter((d) => d.sistema === filters.sistema);
    return {
      chart1Data: pivotByAmbit(filtered),
      chart1Lines: buildAmbitLines(filtered),
    };
  }, [yearFiltered, filters.sistema]);

  const { chart2Data, chart2Lines } = useMemo(() => {
    const filtered = yearFiltered.filter((d) => d.ambit_estudi === filters.ambit);
    return {
      chart2Data: pivotBySistema(filtered),
      chart2Lines: buildSistemaLines(filtered),
    };
  }, [yearFiltered, filters.ambit]);

  return { chart1Data, chart1Lines, chart2Data, chart2Lines, availableAmbits, loading, error };
}
